import { Component, DestroyRef, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { ForbiddenNamesDirective } from '@zero/shared/ui-directives';
import { TransactionAuthorizationComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { AuthPayloadEnrollDevice, AuthorizationData, TransactionAuthorizationResult } from '@zero/shared/util-models';
import { DataService } from '@zero/shared/util-web-app-services';
import { POLLING_FOR_AUTHORIZATION_INTERVAL_MS } from 'constants/shared-constants';
import { interval } from 'rxjs';
import { filter, takeWhile } from 'rxjs/operators';

@Component({
  selector: 'self-service-apptan-enrollment',
  templateUrl: './app-tan-enrollment.component.html',
  imports: [FormsModule, TransactionAuthorizationComponent, ButtonComponent, ForbiddenNamesDirective, TranslocoPipe],
})
export class AppTanEnrollmentComponent implements OnInit {
  public customerDeviceName?: string;
  public newDeviceFound = false;
  public enrollmentCredential?: string;
  public imgData?: string;
  public authorizationMode = false;
  public dataToAuthorize?: AuthorizationData<AuthPayloadEnrollDevice>;
  public deviceNameList: string[];

  @Output() public enrollmentCanceledOrFinished = new EventEmitter<void>();

  #newDeviceToWatch: string;
  readonly #dataService = inject(DataService);
  readonly #destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.#dataService
      .getEnrolledDevices$()
      .pipe(
        filter((devices) => devices?.length > 0),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((devices) => {
        this.deviceNameList = devices.map((dev) => dev.customerDeviceName.trim().toLowerCase());
        devices.forEach((dev) => {
          if (dev.customerDeviceName === this.#newDeviceToWatch) {
            this.newDeviceFound = true;
          }
        });
      });
  }

  public onSubmit(f: NgForm): void {
    if (f.valid) {
      this.authorizationMode = true;
      this.dataToAuthorize = new AuthorizationData<AuthPayloadEnrollDevice>(new AuthPayloadEnrollDevice(this.customerDeviceName));
    }
  }

  public cancelEnrollment(): void {
    this.enrollmentCanceledOrFinished.emit();
  }

  public finishEnrollment(): void {
    this.enrollmentCanceledOrFinished.emit();
  }

  #startWatchingForNewDevice(): void {
    interval(POLLING_FOR_AUTHORIZATION_INTERVAL_MS)
      .pipe(
        takeWhile(() => !this.#isNewDeviceFound()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#dataService.triggerReloadEnrolledDevices());
  }

  #isNewDeviceFound(): boolean {
    return this.newDeviceFound;
  }

  public authorizedTransactionResult(result: TransactionAuthorizationResult): void {
    if (result.success) {
      this.enrollmentCredential = this.#convertForDisplay(result.data);
      this.#newDeviceToWatch = this.customerDeviceName;
      this.authorizationMode = false;
      this.imgData = result.imgData;
      this.#startWatchingForNewDevice();
    } else if (result.reason === 'CanceledByUser') {
      this.cancelEnrollment();
      this.authorizationMode = false;
      this.enrollmentCredential = undefined;
      this.imgData = undefined;
    }
  }

  #convertForDisplay(value: string): string {
    if (value) {
      const splitted: string[] = value.trim().split('');
      let result = '';
      for (let i = 0; i < splitted.length; i++) {
        result += splitted[i];
        if ((i + 1) % 4 === 0 && i < splitted.length - 1) {
          result += '-';
        }
      }
      return result;
    } else {
      return '';
    }
  }
}
